import { useState } from 'react';
import { Spinner } from '@monksflow/monks-ui';
import { ChevronDownIcon, ChevronRightIcon, Trash2Icon } from 'lucide-react';
import type { GwiResearchQaResponseDto } from '@inference.engine/api-client/web';

import {
  usePersonaResearchHistory,
  useDeletePersonaResearch,
  type GwiResearchCategory,
} from '@/entities/gwi-research';
import { notificationService } from '@/lib/services/notification.service';

import { formatInsight } from './formatInsight';
import { useTranslation } from 'react-i18next';

interface DataSourcesTabProps {
  personaId: string;
  category?: GwiResearchCategory;
}

function ResearchHistoryRow({
  qa,
  onDelete,
  isDeleting,
}: {
  qa: GwiResearchQaResponseDto;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const { t } = useTranslation('common');

  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="rounded-lg border border-border">
      <div className="flex w-full items-center gap-2 px-4 py-3">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left font-medium"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? (
            <ChevronDownIcon className="size-4" />
          ) : (
            <ChevronRightIcon className="size-4" />
          )}
          <span>{qa.prompt}</span>
        </button>

        {confirming ? (
          <span className="flex shrink-0 items-center gap-3 text-sm">
            <button
              type="button"
              className="text-muted-foreground transition-colors duration-fast ease-out hover:text-foreground"
              onClick={() => setConfirming(false)}
              disabled={isDeleting}
            >
              {t('components.dataSourcesTab.cancel')}
            </button>
            <button
              type="button"
              aria-label={t('components.dataSourcesTab.confirmDelete')}
              className="font-medium text-destructive transition-opacity duration-fast ease-out hover:opacity-80"
              onClick={onDelete}
              disabled={isDeleting}
            >
              {t('components.dataSourcesTab.delete')}
            </button>
          </span>
        ) : (
          <button
            type="button"
            aria-label={t('components.dataSourcesTab.deleteQuestion')}
            className="shrink-0 text-muted-foreground transition-colors duration-fast ease-out hover:text-destructive"
            onClick={() => setConfirming(true)}
          >
            <Trash2Icon className="size-4" />
          </button>
        )}
      </div>

      <div hidden={!expanded} className="flex flex-col gap-2 px-4 pb-4">
        {qa.insights.map((insight, index) => (
          <div
            key={index}
            className="rounded-lg border border-border bg-muted p-3 text-sm text-foreground"
          >
            {formatInsight(insight)}
          </div>
        ))}
      </div>
    </li>
  );
}

export function DataSourcesTab({ personaId, category }: DataSourcesTabProps) {
  const { t } = useTranslation('common');

  const { data, isLoading } = usePersonaResearchHistory(personaId, category);
  const deleteResearch = useDeletePersonaResearch(personaId, category);

  const handleDelete = async (id: string) => {
    try {
      await deleteResearch.mutateAsync(id);
      notificationService.showSuccess(t('components.dataSourcesTab.questionDeleted'));
    } catch {
      notificationService.showError(t('components.dataSourcesTab.failedToDeleteQuestion'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {t('components.dataSourcesTab.youHavenTAskedGwi')}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {data.map((qa) => (
        <ResearchHistoryRow
          key={qa.id}
          qa={qa}
          isDeleting={deleteResearch.isPending}
          onDelete={() => void handleDelete(qa.id)}
        />
      ))}
    </ul>
  );
}

DataSourcesTab.displayName = 'DataSourcesTab';
